import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type CatalogSlug,
  type CitySlug,
  COLOMBIAN_CITIES,
  MAX_CUSTOM_CITY_NAME,
  OTHER_CITY_SLUG,
  computeRank,
} from "./cities";
import { RateLimitedError, useCityRequestCounts, useSubmitCityRequest } from "./hooks";
import { RateLimitedError as ApiRateLimitedError, SpamDetectedError } from "../../lib/api";
import { TurnstileChallenge } from "../reports/TurnstileChallenge";

type PickerState = "closed" | "list" | "other";

type Selection = { slug: CitySlug; name: string; isCustom: boolean };

type Confirmation =
  | { kind: "ranked"; label: string; rank: number }
  | { kind: "received"; label: string }
  | { kind: "duplicate"; label: string };

type Outcome =
  | { kind: "error"; message: string }
  | { kind: "confirmation"; confirmation: Confirmation };

export function RequestYourCity() {
  const { t, i18n } = useTranslation();
  const counts = useCityRequestCounts();
  const submit = useSubmitCityRequest();
  const listboxId = useId();

  const [picker, setPicker] = useState<PickerState>("closed");
  const [selection, setSelection] = useState<Selection | null>(null);
  const [customDraft, setCustomDraft] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (picker === "closed") return;
    function onPointer(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setPicker("closed");
    }
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, [picker]);

  useEffect(() => {
    if (picker === "other") customInputRef.current?.focus();
  }, [picker]);

  const total = useMemo(() => {
    if (!counts.data) return 0;
    return Object.values(counts.data).reduce((s, n) => s + (n ?? 0), 0);
  }, [counts.data]);

  const tileOptions = useMemo(
    () => COLOMBIAN_CITIES.filter((c) => !c.live),
    [],
  );
  const otherOptionIndex = tileOptions.length;

  function setActive(i: number) {
    if (activeIndex !== i) setActiveIndex(i);
  }

  function openList() {
    setActiveIndex(0);
    setPicker("list");
  }

  function selectCatalog(slug: CatalogSlug, name: string) {
    setSelection({ slug, name, isCustom: false });
    setPicker("closed");
    triggerRef.current?.focus();
  }

  function chooseOther() {
    setPicker("other");
  }

  function commitOther() {
    const trimmed = customDraft.trim();
    if (!trimmed) return;
    setSelection({ slug: OTHER_CITY_SLUG, name: trimmed, isCustom: true });
    setPicker("closed");
    triggerRef.current?.focus();
  }

  async function onSubmit() {
    if (!selection || submit.isPending) return;
    try {
      await submit.mutateAsync({
        citySlug: selection.slug,
        customName: selection.isCustom ? selection.name : undefined,
        locale: i18n.language,
        cfTurnstileResponse: turnstileToken ?? undefined,
      });
      const rank = computeRank(selection.slug, counts.data ?? {});
      const confirmation: Confirmation =
        rank !== null
          ? { kind: "ranked", label: selection.name, rank }
          : { kind: "received", label: selection.name };
      setOutcome({ kind: "confirmation", confirmation });
      setSelection(null);
      setCustomDraft("");
    } catch (err) {
      if (err instanceof RateLimitedError || err instanceof ApiRateLimitedError) {
        setOutcome({
          kind: "confirmation",
          confirmation: { kind: "duplicate", label: selection.name },
        });
        return;
      }
      if (err instanceof SpamDetectedError) {
        // Turnstile token is one-time-use — reset so the user gets a fresh challenge.
        setTurnstileToken(null);
        setOutcome({ kind: "error", message: t("landing.next_city.error_captcha") });
        return;
      }
      console.error("[cityRequest] submit failed", err);
      setOutcome({ kind: "error", message: t("landing.next_city.error_generic") });
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (picker === "other") return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (picker === "closed") {
        openList();
        return;
      }
      setActive(Math.min(activeIndex + 1, otherOptionIndex));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (picker === "closed") {
        openList();
        return;
      }
      setActive(Math.max(activeIndex - 1, 0));
    } else if (e.key === "Enter" || e.key === " ") {
      if (picker === "list") {
        e.preventDefault();
        if (activeIndex < tileOptions.length) {
          const c = tileOptions[activeIndex];
          if (c) selectCatalog(c.slug, c.name);
        } else {
          chooseOther();
        }
      }
    } else if (e.key === "Escape" && picker === "list") {
      e.preventDefault();
      setPicker("closed");
    } else if (e.key === "/" && picker === "list") {
      e.preventDefault();
      chooseOther();
    }
  }

  const tileCountLabel = (n: number) =>
    n > 0
      ? t("landing.next_city.tile_count", { count: n })
      : t("landing.next_city.tile_first");

  const activeOptionId = `${listboxId}-opt-${activeIndex}`;

  return (
    <section className="hairline relative overflow-hidden border-t px-5 py-24 sm:px-10 sm:py-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-ember/8 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-volt/8 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-5xl gap-12 md:grid-cols-[1fr_1.4fr] md:gap-16">
        <div className="reveal">
          <p className="eyebrow inline-flex items-center gap-2 text-volt">
            <span className="live-dot" aria-hidden="true" />
            {t("landing.next_city.eyebrow")}
          </p>
          <h2 className="display mt-6 text-4xl leading-[0.95] sm:text-5xl md:text-6xl">
            {t("landing.next_city.title_live")}{" "}
            <span className="relative inline-block text-volt">
              Medellín
              <ArrowTrace />
            </span>
            .
            <span className="block text-fg-mute">
              {t("landing.next_city.title_next")}
            </span>
          </h2>
          <p className="num mt-8 max-w-sm text-[11px] uppercase tracking-[0.18em] text-fg-faint">
            {t("landing.next_city.helper")}
          </p>
        </div>

        <div ref={wrapRef} className="reveal reveal-delay-2 relative">
          <div className="surface-cinematic relative p-6 sm:p-8">
            <p className="eyebrow text-fg-faint">
              {t("landing.next_city.picker_label")}
            </p>

            <button
              ref={triggerRef}
              type="button"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={picker !== "closed"}
              aria-controls={listboxId}
              aria-activedescendant={
                picker === "list" ? activeOptionId : undefined
              }
              onKeyDown={onTriggerKeyDown}
              onClick={() => (picker === "closed" ? openList() : setPicker("closed"))}
              className="mt-4 flex w-full items-center justify-between gap-4 rounded-md border border-line-strong bg-surface-sunk px-5 py-4 text-left hover:border-line-volt"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span aria-hidden="true" className="text-volt text-lg">
                  ◆
                </span>
                <span className="display truncate text-2xl tracking-tight text-fg sm:text-3xl">
                  {selection
                    ? selection.name
                    : t("landing.next_city.picker_placeholder")}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={`num text-xs text-fg-mute transition-transform duration-300 ${
                  picker !== "closed" ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {picker === "list" && (
              <div
                id={listboxId}
                role="listbox"
                aria-label={t("landing.next_city.listbox_label")}
                className="surface-cinematic pop-in absolute left-0 right-0 z-20 mt-2 max-h-[26rem] overflow-y-auto p-2 shadow-2xl"
              >
                {tileOptions.map((c, i) => (
                  <PickerRow
                    key={c.slug}
                    id={`${listboxId}-opt-${i}`}
                    label={c.name}
                    hint={tileCountLabel(counts.data?.[c.slug] ?? 0)}
                    active={i === activeIndex}
                    onSelect={() => selectCatalog(c.slug, c.name)}
                    onHover={() => setActive(i)}
                    delayMs={i * 28}
                  />
                ))}
                <PickerRow
                  id={`${listboxId}-opt-${otherOptionIndex}`}
                  label={t("landing.next_city.other_label")}
                  hint={t("landing.next_city.other_hint")}
                  active={activeIndex === otherOptionIndex}
                  dashed
                  onSelect={chooseOther}
                  onHover={() => setActive(otherOptionIndex)}
                  delayMs={otherOptionIndex * 28}
                />

                <div className="mt-3 flex items-center justify-between rounded-md border border-line/60 bg-surface-sunk/60 px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="live-dot" aria-hidden="true" />
                    <span className="display text-base text-fg-mute">
                      Medellín
                    </span>
                  </span>
                  <span className="num text-[10px] uppercase tracking-[0.16em] text-volt">
                    {t("landing.next_city.live_badge")}
                  </span>
                </div>
              </div>
            )}

            {picker === "other" && (
              <div className="surface-cinematic pop-in absolute left-0 right-0 z-20 mt-2 p-4 shadow-2xl">
                <label
                  htmlFor="other-city-input"
                  className="eyebrow text-fg-faint"
                >
                  {t("landing.next_city.other_input_label")}
                </label>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    id="other-city-input"
                    ref={customInputRef}
                    type="text"
                    value={customDraft}
                    onChange={(e) =>
                      setCustomDraft(e.target.value.slice(0, MAX_CUSTOM_CITY_NAME))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitOther();
                      } else if (e.key === "Escape") {
                        setPicker("list");
                      }
                    }}
                    maxLength={MAX_CUSTOM_CITY_NAME}
                    placeholder={t("landing.next_city.other_placeholder") ?? ""}
                    className="display flex-1 rounded-md border border-line-strong bg-surface-sunk px-4 py-3 text-2xl tracking-tight text-fg placeholder:text-fg-faint focus:border-line-volt focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={commitOther}
                    disabled={!customDraft.trim()}
                    className="btn-ghost px-4 py-3 text-sm disabled:opacity-40"
                  >
                    {t("landing.next_city.other_confirm")}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setPicker("list")}
                  className="num mt-3 text-[10px] uppercase tracking-[0.18em] text-fg-mute underline decoration-line-strong underline-offset-4 hover:text-fg"
                >
                  ← {t("landing.next_city.back_to_list")}
                </button>
              </div>
            )}

            <div className="mt-6">
              <TurnstileChallenge onVerify={setTurnstileToken} />
            </div>

            <button
              type="button"
              onClick={onSubmit}
              disabled={!selection || !turnstileToken || submit.isPending}
              className="btn-volt group mt-6 inline-flex items-center gap-3 px-6 py-3.5 text-sm tracking-tight disabled:cursor-not-allowed disabled:bg-line-strong disabled:text-fg-faint disabled:hover:shadow-none"
            >
              {submit.isPending
                ? t("landing.next_city.cta_pending")
                : t("landing.next_city.cta_submit")}
              <span
                aria-hidden="true"
                className="transition-transform duration-300 ease-out group-hover:translate-x-0.5"
              >
                →
              </span>
            </button>
          </div>

          {outcome?.kind === "error" && (
            <p
              role="alert"
              className="num reveal-soft mt-4 flex items-center gap-3 text-[12px] uppercase tracking-[0.16em] text-ember"
            >
              <span aria-hidden="true">!</span>
              {outcome.message}
            </p>
          )}

          {outcome?.kind === "confirmation" && (
            <ConfirmationLine confirmation={outcome.confirmation} />
          )}

          <p className="num mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-fg-faint">
            <span aria-hidden="true" className="h-px w-8 bg-line-strong" />
            {counts.isError
              ? t("landing.next_city.total_unavailable")
              : total === 0
                ? t("landing.next_city.total_first")
                : t("landing.next_city.total_count", { count: total })}
            <span aria-hidden="true" className="h-px flex-1 bg-line" />
          </p>
        </div>
      </div>
    </section>
  );
}

function ConfirmationLine({ confirmation }: { confirmation: Confirmation }) {
  const { t } = useTranslation();
  const message =
    confirmation.kind === "ranked"
      ? t("landing.next_city.confirmation_ranked", {
          city: confirmation.label,
          rank: confirmation.rank,
        })
      : confirmation.kind === "duplicate"
        ? t("landing.next_city.confirmation_duplicate", {
            city: confirmation.label,
          })
        : t("landing.next_city.confirmation_received", {
            city: confirmation.label,
          });
  return (
    <p
      role="status"
      className="num reveal-soft mt-4 flex items-center gap-3 text-[12px] uppercase tracking-[0.16em] text-volt"
    >
      <span className="live-dot" aria-hidden="true" />
      {message}
    </p>
  );
}

type PickerRowProps = {
  id: string;
  label: string;
  hint: string;
  active: boolean;
  dashed?: boolean;
  onSelect: () => void;
  onHover: () => void;
  delayMs: number;
};

function PickerRow({
  id,
  label,
  hint,
  active,
  dashed,
  onSelect,
  onHover,
  delayMs,
}: PickerRowProps) {
  const borderClass = dashed
    ? active
      ? "border-dashed border-line-volt bg-surface-raised"
      : "border-dashed border-line"
    : active
      ? "border-line-volt bg-surface-raised"
      : "border-transparent hover:border-line";
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onClick={onSelect}
      onMouseEnter={onHover}
      className={`stagger-in mt-1 flex w-full items-center justify-between gap-3 rounded-md border px-4 py-3 text-left ${borderClass}`}
      style={{ "--stagger-delay": `${delayMs}ms` } as React.CSSProperties}
    >
      <span className="display text-lg text-fg">{label}</span>
      <span
        aria-hidden="true"
        className={`num text-[10px] uppercase tracking-[0.16em] ${dashed ? "text-fg-mute" : "text-fg-mute"}`}
      >
        {hint}
      </span>
    </button>
  );
}

// Decorative lime arc from "Medellín" to the picker. Stroke-dasharray
// gives the etch-in effect on first reveal.
function ArrowTrace() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute -right-12 top-full hidden h-16 w-32 md:block"
      viewBox="0 0 128 64"
      fill="none"
    >
      <path
        d="M 4 8 C 40 8, 60 32, 124 56"
        className="trace-volt text-volt/60"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M 116 50 L 124 56 L 118 62"
        className="trace-arrow text-volt/60"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
